import { runtimeConfig } from '@/core/runtimeConfig';
import { clearAppToken, getAppAccessToken } from './appToken';

/**
 * Returns a valid APP access token for API calls, refreshing the app token pair via
 * `appToken.getAppAccessToken` if the cached one has expired. Resolves `null` when no app
 * session exists at all.
 *
 * Unlike the former MSAL-backed implementation, this never forces an interactive redirect: the
 * only "silent" re-authentication path is the cookie-based refresh `RequireAuth` runs once at
 * boot (work-item 08) — not from here, since every call site of this function (the HTTP client's
 * auth middleware) must stay non-interactive.
 */
export async function getAccessTokenSilent(): Promise<string | null> {
  return getAppAccessToken();
}

/**
 * Logs the current session out: calls the backend `POST /auth/logout` endpoint (204, no body) so
 * the HttpOnly refresh cookie is actually deleted server-side — the client cannot delete an
 * HttpOnly cookie itself, so a bare store-clear would leave the cookie alive (see work-item 07
 * Security notes). Uses a plain `fetch` with `credentials: 'include'`, the same pattern as
 * `appToken.exchange`/`refresh`, instead of the openapi-fetch client, to avoid looping back into
 * this token provider's own auth middleware.
 *
 * The in-memory app token pair is cleared unconditionally, even if the backend call fails
 * (network error or non-2xx): the user must never stay stuck "logged in" client-side just because
 * the server-side cookie clear didn't go through. Replaces the former MSAL `logoutRedirect` and
 * the Demo-Login branch — there is no outbound redirect here (AK7 no-redirect-leak guardrail).
 *
 * The former "Angemeldet bleiben" `localStorage` cleanup (`nativeAuthSession.clearRememberMe`)
 * is intentionally no longer called here — that persistence mechanism is superseded by the
 * cookie-based `rememberMe` transport (work-item 06); `nativeAuthSession.ts` itself was removed
 * in work-item 10, no localStorage/sessionStorage session persistence remains anywhere.
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${runtimeConfig.apiBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort: a network failure calling the logout endpoint must not prevent the
    // client-side logout below — the in-memory store is still cleared in `finally`.
  } finally {
    clearAppToken();
  }
}
